/**
 * Google Tag Manager utility for tracking purchase events.
 *
 * Pushes a "purchase" event to the GTM dataLayer so it can be
 * forwarded to Google Ads (or any other GTM-connected destination).
 *
 * The dataLayer array is lazily initialised – if GTM has not yet
 * loaded the snippet, the push is queued and will be picked up as
 * soon as the container loads.
 */

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

export interface PurchaseEventParams {
  /** Unique order / transaction ID (e.g. Stripe PaymentIntent ID) */
  transaction_id: string;
  /** Payment amount in major currency units (e.g. dollars, not cents) */
  value: number;
  /** ISO 4217 currency code – defaults to "USD" */
  currency?: string;
}

/**
 * Push a purchase event to the GTM dataLayer.
 *
 * Safe to call even when GTM is not loaded – the event will be
 * queued in `window.dataLayer` and picked up when the container
 * initialises.
 */
export function trackPurchase({ transaction_id, value, currency = 'USD' }: PurchaseEventParams): void {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'purchase',
      transaction_id,
      value,
      currency: currency.toUpperCase()
    });
    console.log('[GTM] purchase event pushed', { transaction_id, value, currency });
  } catch (err) {
    // Non-critical – never let analytics break the app
    console.warn('[GTM] Failed to push purchase event', err);
  }
}
